<script setup lang="ts">
/**
 * The getting-started guide, in a corner of every page: the steps in order,
 * the current one open with its explanation, a button to the page where it
 * is done, and « Next ». A step ticks itself when the data says it is done;
 * one can also be marked done by hand. Closed, it reopens from the help.
 */
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useFormat } from '@/lib/format'
import Button from '@/ui/Button.vue'
import Icon from '@/ui/Icon.vue'
import Progress from '@/ui/Progress.vue'
import { STEP_LINKS, useGuide, useGuideAction, type GuideStep, type StepKey } from './onboarding'

const { t } = useI18n()
const f = useFormat()
const route = useRoute()
const router = useRouter()
const guide = useGuide()
const act = useGuideAction()
const open = ref(true)
const current = ref<StepKey | null>(null)

const g = computed(() => guide.data.value)
const visible = computed(() => Boolean(g.value && g.value.welcomeSeen && !g.value.dismissed && route.path !== '/welcome'))
const doneCount = computed(() => g.value?.steps.filter((s) => s.done).length ?? 0)
const total = computed(() => g.value?.steps.length ?? 0)
// Follows the first step not done, until the admin picks another.
watch(() => g.value?.next, (n) => { current.value = n ?? null }, { immediate: true })
const step = computed<GuideStep | null>(() => g.value?.steps.find((s) => s.key === current.value) ?? null)
const index = computed(() => (step.value ? g.value!.steps.indexOf(step.value) : -1))

function figures(s: GuideStep): string | null {
  const x = s.figures
  switch (s.key) {
    case 'team': return t('onboarding.figures.team', { agents: f.number(Number(x.agents)), supervisors: f.number(Number(x.supervisors)) })
    case 'contacts': return t('onboarding.figures.contacts', { outlets: f.number(Number(x.outlets)), contacts: f.number(Number(x.contacts)) })
    case 'catalog': return t('onboarding.figures.catalog', { n: f.number(Number(x.products)) })
    case 'campaign': return t('onboarding.figures.campaign', { n: f.number(Number(x.campaigns)), active: f.number(Number(x.active ?? 0)),
      withList: f.number(Number(x.withList)), withAgents: f.number(Number(x.withAgents ?? 0)) })
    case 'script': return t('onboarding.figures.script', { n: f.number(Number(x.published)) })
    case 'firstCall': return t('onboarding.figures.calls', { n: f.number(Number(x.calls)) })
    default: return null
  }
}
function next() {
  if (!step.value || !g.value) return
  // « Next » on a step not done yet: the admin says it is done (or skips it).
  if (!step.value.done) act.mutate({ step: step.value.key, done: true })
  else current.value = g.value.steps.slice(index.value + 1).find((s) => !s.done)?.key ?? g.value.steps[index.value + 1]?.key ?? null
}
const go = (to: string) => router.push(to)
</script>

<template>
  <aside v-if="visible && g" class="fixed bottom-4 end-4 z-40 w-[360px] max-w-[calc(100vw-2rem)] rounded-card border border-border bg-card shadow-lg"
         :aria-label="t('onboarding.guide.title')">
    <button type="button" class="flex w-full items-center gap-2.5 px-4 py-3 text-start" :aria-expanded="open" @click="open = !open">
      <Icon name="help" :size="16" class="text-primary" />
      <span class="grid flex-1 gap-1">
        <span class="text-[13.5px] font-semibold">{{ g.completed ? t('onboarding.guide.doneTitle') : t('onboarding.guide.title') }}</span>
        <Progress :value="total ? doneCount / total * 100 : 0" :label="t('onboarding.guide.progress', { done: doneCount, total })" />
      </span>
      <span class="text-[12px] text-muted-foreground">{{ doneCount }}/{{ total }}</span>
      <Icon :name="open ? 'chevron-down' : 'chevron-up'" :size="16" />
    </button>

    <div v-if="open" class="grid gap-3 border-t border-border px-4 py-3">
      <p v-if="g.completed" class="m-0 text-[13px]">{{ t('onboarding.guide.doneBody') }}</p>
      <ol class="m-0 grid list-none gap-1 p-0">
        <li class="motion-row" v-for="(s, i) in g.steps" :style="{ '--i': i }" :key="s.key">
          <button type="button" class="flex w-full items-center gap-2 rounded-input px-2 py-1.5 text-start text-[13px] hover:bg-muted"
                  :class="s.key === current ? 'bg-primary/10 font-medium text-primary' : s.done ? 'text-muted-foreground' : ''"
                  @click="current = s.key">
            <span class="grid size-5 shrink-0 place-items-center rounded-full text-[11px]"
                  :class="s.done ? 'bg-success text-white' : 'border border-border'">
              <Icon v-if="s.done" name="check" :size="12" /><template v-else>{{ i + 1 }}</template>
            </span>
            <span :class="s.done && s.key !== current ? 'line-through' : ''">{{ t(`onboarding.step.${s.key}.title`) }}</span>
          </button>
          <div v-if="s.key === current && step" class="grid gap-2 px-2 pb-1 pt-2">
            <p class="m-0 text-[12.5px] text-muted-foreground">{{ t(`onboarding.step.${s.key}.body`) }}</p>
            <p v-if="figures(s)" class="m-0 text-[12px]">{{ figures(s) }}</p>
            <div class="flex flex-wrap gap-1.5">
              <Button size="sm" :icon-start="STEP_LINKS[s.key].icon" @click="go(STEP_LINKS[s.key].to)">{{ t(`onboarding.step.${s.key}.action`) }}</Button>
              <Button v-if="STEP_LINKS[s.key].alt" size="sm" variant="outline" @click="go(STEP_LINKS[s.key].alt!.to)">
                {{ t(`onboarding.step.${s.key}.alt`) }}
              </Button>
            </div>
            <div class="flex items-center gap-2 pt-1">
              <Button v-if="s.byHand" variant="ghost" size="sm" @click="act.mutate({ step: s.key, done: false })">{{ t('onboarding.guide.undo') }}</Button>
              <span class="flex-1" />
              <Button size="sm" variant="secondary" icon-end="chevron-right" :loading="act.isPending.value" @click="next">
                {{ s.done ? t('onboarding.guide.next') : t('onboarding.guide.markDone') }}
              </Button>
            </div>
          </div>
        </li>
      </ol>
      <div class="flex justify-end border-t border-border pt-2">
        <Button variant="ghost" size="sm" @click="act.mutate('dismiss')">{{ t('onboarding.guide.close') }}</Button>
      </div>
    </div>
  </aside>
</template>
